import _, {last, uniq} from 'lodash';
import {Edge} from 'reactflow';
import uuid from 'uuid-random';

import {move} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {
  DecisionTreeModel,
  DecisionTreeNodeFrontChat,
} from '../../../../libs/shared/core/src/models/api/decisionTreeModel';
import {
  DecisionTreeNodeDataSubtreeFrontChat,
  DecisionTreeNodeDataTagFrontChat,
  isDecisionTreeBranchNodeDataFrontChat,
  isEndNodeData,
  isPromptReferenceNodeData,
  isStartNodeData,
  NodeDataFrontChatTypesEnum,
} from '../../../../libs/shared/core/src/models/api/decisionTreeNodeDataModel';
import {
  getPathToNode,
  getSubtreeIds,
  isTagNode,
  makeNode,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {Expression} from '../../../settings/editors/expressions/expressionModel';
import {
  buildDefaultParseExpressionOptions,
  computeParseExpressionResultType,
} from './expressions/expressionHelpers';
import {ChatBotBuilderNode, ChatBotBuilderNodeData} from './types';

/*
 * Enums.
 */

// WARNING: Never mutate the values in this enum. These values are expected downstream in front-chat
// so that the appropriate labels can be rendered, and the appropriate analytics metadata can be sent.
export enum SemanticSearchOptionsEnum {
  YES = 'Yes',
  NO = 'No',
}

/*
 * Helpers.
 */

export const makeChatBotBuilderNode = makeNode<ChatBotBuilderNodeData>;

export function makeChatBotBuilderDefaultNodeData(
  nodeDataType: NodeDataFrontChatTypesEnum,
  pathToParentNode: ReadonlyArray<ChatBotBuilderNode> | undefined,
  expressions: ReadonlyArray<Expression>,
): ChatBotBuilderNodeData {
  switch (nodeDataType) {
    case NodeDataFrontChatTypesEnum.MESSAGE:
      return {
        type: NodeDataFrontChatTypesEnum.MESSAGE,
        message: '',
      };
    case NodeDataFrontChatTypesEnum.MULTI_CHOICE:
      return {
        type: NodeDataFrontChatTypesEnum.MULTI_CHOICE,
        message: '',
        options: [
          {
            value: '',
            sourceHandle: uuid(),
          },
          {
            value: '',
            sourceHandle: uuid(),
          },
        ],
      };
    case NodeDataFrontChatTypesEnum.PROMPT:
      return {
        type: NodeDataFrontChatTypesEnum.PROMPT,
        message: '',
      };
    case NodeDataFrontChatTypesEnum.CUSTOM_FIELDS:
      return {type: NodeDataFrontChatTypesEnum.CUSTOM_FIELDS, customFields: []};
    case NodeDataFrontChatTypesEnum.PARSE_TEXT: {
      const lastPromptId = findLastPromptId(pathToParentNode);

      return {
        type: NodeDataFrontChatTypesEnum.PARSE_TEXT,
        promptNodeId: lastPromptId,
        options: [
          {
            value: [],
            sourceHandle: uuid(),
          },
          {
            value: [],
            sourceHandle: uuid(),
          },
        ],
      };
    }
    case NodeDataFrontChatTypesEnum.TAG:
      return {
        type: NodeDataFrontChatTypesEnum.TAG,
        tagIds: [],
      };
    case NodeDataFrontChatTypesEnum.SEMANTIC_SEARCH: {
      const lastPromptId = findLastPromptId(pathToParentNode);

      return {
        type: NodeDataFrontChatTypesEnum.SEMANTIC_SEARCH,
        promptNodeId: lastPromptId,
        options: [
          {
            value: SemanticSearchOptionsEnum.YES,
            sourceHandle: uuid(),
          },
          {
            value: SemanticSearchOptionsEnum.NO,
            sourceHandle: uuid(),
          },
        ],
      };
    }
    case NodeDataFrontChatTypesEnum.SUBTREE:
      return makeSubtreeNodeData();
    case NodeDataFrontChatTypesEnum.PARSE_EXPRESSION: {
      const firstExpression = expressions[0];
      if (!firstExpression) {
        throw new Error('Expect to have at least one expression to create a parse expression node');
      }

      const resultType = computeParseExpressionResultType(firstExpression);

      return {
        type: NodeDataFrontChatTypesEnum.PARSE_EXPRESSION,
        expressionId: firstExpression?.id,
        resultType,
        options: buildDefaultParseExpressionOptions(resultType),
      };
    }
    default:
      throw new Error(`Cannot create node data of type: ${nodeDataType}`);
  }
}

export function makeSubtreeNodeData(): DecisionTreeNodeDataSubtreeFrontChat {
  return {
    type: NodeDataFrontChatTypesEnum.SUBTREE,
    options: [
      {
        sourceHandle: uuid(),
      },
    ],
  };
}
/**
 * Find the most recent prompt node in this branch if it exists.
 */
export function findLastPromptId(pathToParentNode?: ReadonlyArray<ChatBotBuilderNode>) {
  if (!pathToParentNode) {
    return undefined;
  }

  return (
    _(pathToParentNode).findLast((node) => node.data.type === NodeDataFrontChatTypesEnum.PROMPT)?.id ??
    undefined
  );
}

/**
 * Given a node that references a prompt node, re-compute its promptNodeId. After a PROMPT node is added or deleted,
 * each referring node may have to update this value as these node types automatically use the most recent PROMPT node in their branch.
 *
 * We may optionally ignore a prompt node if it has been deleted and it should not appear as a result from findLastPromptId.
 */

export function maybeUpdatePromptReference(
  node: ChatBotBuilderNode,
  nodes: Array<ChatBotBuilderNode>,
  edges: Array<Edge>,
) {
  // no need to update promptNodeId for nodes that are not prompt references
  if (!isPromptReferenceNodeData(node.data)) {
    return node;
  }

  const path = getPathToNode(node, nodes, edges);
  const promptNodeId = findLastPromptId(path);
  const updatedPromptReferenceNode = {
    ...node,
    data: {
      ...node.data,
      promptNodeId,
    },
  };

  return updatedPromptReferenceNode;
}

/**
 * Returns only prompt reference nodes after re-computing its promptNodeId.
 */
export function computePromptNodeIdForPromptReferenceNodes(
  nodes: Array<ChatBotBuilderNode>,
  edges: Array<Edge>,
) {
  const promptReferenceNodes = nodes.filter((node) => isPromptReferenceNodeData(node.data));
  // Filter through nodes and update only the prompt reference nodes with a potential new prompt node.
  return promptReferenceNodes.map((node) => maybeUpdatePromptReference(node, nodes, edges));
}

/**
 * Gather any prompt reference nodes without a prompt earlier in its path.
 *
 * Note: A subtree can be saved with a prompt reference node without a prompt, and this check will then return the bad nodes for that subtree.
 */
export function gatherPromptReferenceNodesWithoutPrompt(decisionTree?: DecisionTreeModel) {
  if (!decisionTree || !decisionTree.treeData) {
    return [];
  }

  const {nodes, edges} = decisionTree.treeData;

  return nodes.filter((node) => {
    if (!isPromptReferenceNodeData(node.data)) {
      return false;
    }
    const path = getPathToNode(node, nodes.slice(), edges.slice());
    const promptNode = findLastPromptId(path);
    return !promptNode;
  });
}

/**
 * Given a list of nodes, retrieve a unique and flattened list of all tag ids that
 * exist among all nodes with a data type of TAG.
 */
function gatherNodesTagIds(nodes: ReadonlyArray<DecisionTreeNodeFrontChat>) {
  return _(nodes)
    .map((pathNode) => pathNode.data)
    .filter(
      (nodeData): nodeData is DecisionTreeNodeDataTagFrontChat =>
        nodeData.type === NodeDataFrontChatTypesEnum.TAG,
    )
    .map((nodeData) => nodeData.tagIds)
    .flatten()
    .uniq()
    .value();
}

/**
 * Uses gatherNodesTagIds, but also includes the tag ids from any subtree nodes or any subtree tags that may apply
 */
export function gatherNodeTagIdsIncludeSubtrees(
  nodes: ReadonlyArray<DecisionTreeNodeFrontChat>,
  subtrees: ReadonlyArray<DecisionTreeModel>,
) {
  const {tagIds, maybeTagIds} = gatherSubtreeTags(nodes, subtrees);

  return {
    tagIds: uniq([...gatherNodesTagIds(nodes), ...tagIds]),
    maybeTagIds: uniq(maybeTagIds),
  };
}

function gatherSubtreeTags(
  nodes: ReadonlyArray<DecisionTreeNodeFrontChat>,
  subtrees: ReadonlyArray<DecisionTreeModel>,
) {
  const subtreeIds = getSubtreeIds(nodes);
  const subtreeTags: number[] = [];
  const subtreeMaybeTags: number[] = [];

  subtreeIds.forEach((subtreeId) => {
    const subtree = subtrees.find((s) => s.id === subtreeId);
    // if a branch node is found, then all tags after it
    // are 'maybe tags' (tags that maybe apply depending on the path the user takes)
    let isAfterBranchNode = false;

    if (!subtree) {
      return;
    }

    subtree.treeData?.nodes.forEach((node) => {
      if (isDecisionTreeBranchNodeDataFrontChat(node.data)) {
        isAfterBranchNode = true;
        return;
      }
      if (isTagNode(node)) {
        const tagList = isAfterBranchNode ? subtreeMaybeTags : subtreeTags;
        tagList.push(...node.data.tagIds);
      }
    });
  });

  return {
    tagIds: subtreeTags,
    maybeTagIds: subtreeMaybeTags,
  };
}

/**
 * Move an item in an array to a new index, while preserving the values at each location of the array
 * for the keys specified in keysToPreserve.
 */
export function moveAndPreserve<T>(
  array: ReadonlyArray<T>,
  newIndex: number,
  item: T,
  keysToPreserve: ReadonlyArray<keyof T>,
) {
  // For each item in array, build a partial object of the preserved values to spread back in later.
  const preservedValuesArray: ReadonlyArray<Partial<T>> = array.map((arrayItem) =>
    keysToPreserve.reduce<Partial<T>>(
      (preservedValues, key) => ({...preservedValues, [key]: arrayItem[key]}),
      {},
    ),
  );

  const movedArray = move(array, newIndex, item);

  // Spread the preserved values back into each item of the moved array before returning.
  return movedArray.map((arrayItem, index) => {
    const heldValues = preservedValuesArray[index];
    return {...arrayItem, ...heldValues};
  });
}

export function computeNumSteps(decisionTree: DecisionTreeModel) {
  return (
    decisionTree.treeData?.nodes.filter((node) => !isStartNodeData(node.data) && !isEndNodeData(node.data))
      .length ?? 0
  );
}

/** Checks if the most recent node on the path leading up to a node is a certain type. */
export function isLastNodeOfType(
  nodeDataType: NodeDataFrontChatTypesEnum,
  pathToParentNode?: ReadonlyArray<ChatBotBuilderNode>,
) {
  return last(pathToParentNode)?.data.type === nodeDataType;
}

/** Checks if one of the direct children of a node is a certain type. */
export function isSomeChildNodesOfType(
  nodeDataType: NodeDataFrontChatTypesEnum,
  childNodeDescendantPaths: ReadonlyArray<ReadonlyArray<ChatBotBuilderNode>>,
): boolean {
  return childNodeDescendantPaths.some((path) => path[0]?.data.type === nodeDataType);
}
